import { EMPTY_ITEM_RIGHTS_COLLECTION } from "Global/Base/Rights/RightTypes";
import { RequestContext } from "OW.Remoting/Remoting/RequestContext";
import { Event as GlobalEvent } from "../../../../Global/Base/Events";
import { OWDocumentDescription } from "../../../../OW.Remoting/Remoting/DataTypes/OWDocumentDescription";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { LocalizedStringBuilder } from "../DataBuilders/LocalizedStringBuilder";
import { MessageButtonType, MessageType, MessageWindow } from "../MessageWindow";
import { UploadManager } from "../UploadManager";
import { Avatar } from "./Avatar";
import { CommentaryFrameAttachment } from "./CommentaryFrameAttachment";

export class CommentaryFrameInput extends HTMLElement {
    //#region listeners
    public deleteButtonClicked: GlobalEvent<CommentaryFrameInput, MouseEvent> = new GlobalEvent<CommentaryFrameInput, MouseEvent>();
    public sendButtonClicked: GlobalEvent<CommentaryFrameInput, string> = new GlobalEvent<CommentaryFrameInput, string>();
    public valueChanged: GlobalEvent<CommentaryFrameInput, string> = new GlobalEvent<CommentaryFrameInput, string>();
    public fileStartsUploading: GlobalEvent<CommentaryFrameAttachment, void> = new GlobalEvent<CommentaryFrameAttachment, void>();
    public attachmentAdded: GlobalEvent<CommentaryFrameInput, void> = new GlobalEvent<CommentaryFrameInput, void>();
    public fileRemovedMessageShowed: GlobalEvent<CommentaryFrameInput, void> = new GlobalEvent<CommentaryFrameInput, void>();
    //#endregion listeners

    //#region Fields
    protected _attributeData: OWDocumentDescription;
    //private _masterLinkID: string;
    private readonly _cssClass = "commentary-frame-input";
    private readonly _cssClassErrorContainer = this._cssClass + "__file-error-container";
    private readonly _cssClassErrorMessage = this._cssClass + "__error-message";
    private readonly _cssClassErrorButton = this._cssClass + "__error-button";
    private _commentInputBox: HTMLInputElement;
    private _sendCommentButton: HTMLButtonElement;
    private _canInsertComments: boolean;
    private _deleteButton: HTMLButtonElement;
    private _attachmentButton: HTMLButtonElement;
    //private _controlsContainer: HTMLDivElement;
    private _attachmentsContainer: HTMLDivElement;
    private _fileEditAttachmentsList: CommentaryFrameAttachment[] = [];
    private _attachmentClass: string;
    private _attachmentAttribute: string;
    private _actualAttachment: OWObject;
    private _fileError: boolean = false;
    private _fileErrorContainer: HTMLElement;
    private _inputFile: HTMLInputElement;
    private _eventListFnc: any = {};
    private _avatar: Avatar;
    public isReply: boolean;
    public replyTo: string;
    public belongsTo: string;
    public set rightsToInsertComment(canSet: boolean) {
        this._canInsertComments = canSet;
        this._setContentEditableBasedOnRights();
    }

    public get rightsToInsertComment(): boolean {
        return this._canInsertComments;
    }

    public get commentInputBox(): HTMLInputElement {
        return this._commentInputBox;
    }

    public get commentInputBoxText(): string {
        return this._commentInputBox.innerText;
    }

    public get attachmentClass(): string {
        return this._attachmentClass;
    }
    public set attachmentClass(v: string) {
        this._attachmentClass = v;
    }
    public get attachmentAttribute(): string {
        return this._attachmentAttribute;
    }
    public set attachmentAttribute(v: string) {
        this._attachmentAttribute = v;
    }
    //#endregion Fields

    //#region Constructors

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add(this._cssClass);
    }
    //#endregion Constructors

    public initializeComponents(attachmentsEnabled: boolean = true, isReply?: boolean, getRequestContext?: () => RequestContext): void {
        this.isReply = isReply;

        if (this.isReply) {
            this.classList.add(this._cssClass + "__reply");

            // ikona pro smazání
            this._deleteButton = createElement({
                type: "button",
                classes: ["btn", "btn-large", "flat", "item-icon", "icon", "delete", this._cssClass + "__button", this._cssClass + "__bin"],
            }) as HTMLButtonElement;
            Localization.addLocData(this._deleteButton, "GUI-253448", "title"); //Smazat komentář
            this.appendChild(this._deleteButton);

            this._deleteButton.addEventListener("click", this._eventListFnc["clickDeleteButton"] = async (eventData: MouseEvent) => {
                this.deleteButtonClicked.trigger(this, eventData);
                this.dispose();
                this.remove();
            });
        }

        // avatar přihlášeného uživatele
        this._avatar = new Avatar();
        this._avatar.classList.add(this._cssClass + "__button");
        this.appendChild(this._avatar);

        // input box pro zadávání/editaci textu komentáře
        this._commentInputBox = createElement({ type: "div", classes: [this._cssClass + "__input"] }) as HTMLInputElement;
        Localization.addLocData(this._commentInputBox, "GUI-683743", "placeholder"); //Přidat komentář
        this._commentInputBox.contentEditable = this._canInsertComments ? "true" : "false";
        this.appendChild(this._commentInputBox);

        // ikona pro přílohu
        if (attachmentsEnabled) {
            this._inputFile = createElement({ type: "input", attrs: { "type": "file" }, classes: [this._cssClass + "__input-file"] }) as HTMLInputElement;
            this._attachmentButton = createElement({ type: "button", classes: ["btn", "btn-large", "flat", "item-icon", "icon", "doc-attachment", this._cssClass + "__button", this._cssClass + "__attachment-button"] }) as HTMLButtonElement;
            Localization.addLocData(this._attachmentButton, "GUI-683886", "title"); //Vložit přílohu
            this.appendChild(this._attachmentButton);
            this._attachmentButton.addEventListener("click", this._eventListFnc["attachmentButtonOnClick"] = () => {
                this._inputFile.click();
            });

            this._attachmentButton.appendChild(this._inputFile);
            this._inputFile.addEventListener("change", this._eventListFnc["inputFileOnChange"] = async (e: Event) => {
                e.stopPropagation();
                if (this._inputFile.files.length === 0)
                    return;

                this._addAttachment(this._inputFile.files, getRequestContext);
            });

            this._inputFile.addEventListener("click", this._eventListFnc["inputFileOnClick"] = (e: MouseEvent) => {
                EventHelper.stopPropagation(e);
                this._inputFile.value = "";
            });
        }

        // Button pro odeslání komentáře
        this._sendCommentButton = createElement({ type: "button", classes: ["btn", "btn-large", "primary", this._cssClass + "__button", this._cssClass + "__send-button"] }) as HTMLButtonElement;
        Localization.addLocData(this._sendCommentButton, "GUI-683715"); //Odeslat
        this.appendChild(this._sendCommentButton);

        this._sendCommentButton.addEventListener("click", this._eventListFnc["clickSendButton"] = async () => {
            this.sendButtonClicked.trigger(this, this._commentInputBox.innerText);
        });

        this.enableButtons(false);

        this._commentInputBox.addEventListener("input", this._eventListFnc["inputInput"] = async () => {
            this.valueChanged.trigger(this, this._commentInputBox.innerText);
            if (this._commentInputBox.textContent == "")
                this.clearCommentText(false);
            else if (this._sendCommentButton.disabled && this._canInsertComments) {
                this._setSendButtonEnabled(true);
            }
        });

        // Div pro přílohy
        this._attachmentsContainer = createElement({ classes: this._cssClass + "__attachments-container" }) as HTMLDivElement;
        this.appendChild(this._attachmentsContainer);
    }

    public setAvatarNameAndColor(name: string, color: string): void {
        this._avatar.avatarName = name;
        this._avatar.backgroundColor = color;
    }

    //#region Methods
    public getCommentText(): string {
        return this._commentInputBox.innerText;
    }

    public getAttachmentsList(): CommentaryFrameAttachment[] {
        return this._fileEditAttachmentsList;
    }

    /**
     * zaktivní/zneaktivní tlačítko pro odeslání dle hodnoty enabled
     * @param enabled z(ne)aktivní tlačítko
     */
    private _setSendButtonEnabled(enabled: boolean): void {
        this._sendCommentButton.disabled = !enabled;
    }

    /**
     * zaktivní/zneaktivní tlačítko přílohy dle hodnoty enabled
     * @param enabled z(ne)aktivní tlačítko
     */
    public setAttachmentButtonEnabled(enabled: boolean): void {
        if (this._attachmentButton)
            this._attachmentButton.disabled = !enabled;
    }

    public enableButtons(enable: boolean): void {
        this._setSendButtonEnabled(enable);
        this.setAttachmentButtonEnabled(enable);
    }

    /**
    * dle potřeby čistí div contenteditable pro vložení komentáře
    * @param full ne/smaže text; defaultně true
    */
    public clearCommentText(full: boolean = true): void {
        if (full)
            this._commentInputBox.innerText = "";
        if (this._fileEditAttachmentsList.length == 0)
            this._setSendButtonEnabled(false);
        // při odentrování prázdného div contenteditable se vytvoří dva prázdné elementy div
        if (this._commentInputBox.childNodes.length == 1)
            this._commentInputBox.firstChild.remove();
        this.valueChanged.trigger(this, this._commentInputBox.innerText);
    }

    private async _addAttachment(files: FileList, getRequestContext?: () => RequestContext): Promise<void> {
        this._setSendButtonEnabled(false);
        //vytvořit CommentaryFrameAttachment
        const commentaryFrameAttachmentElement = document.createElement("div");
        //plní se pro účely fileeditu
        commentaryFrameAttachmentElement.setAttribute("data-attrname", this.attachmentAttribute);
        const eventFNC: any = {};
        this._actualAttachment = await OWManager.newObject(this._attachmentClass, undefined, getRequestContext?.());
        const fileEditAttachment = new CommentaryFrameAttachment({
            element: commentaryFrameAttachmentElement,
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
        });
        fileEditAttachment.fileStartsUploading.add(eventFNC["fileStartsUploading"] = (sender: CommentaryFrameAttachment) => {
            this.fileStartsUploading.trigger(sender);
        });
        fileEditAttachment.fileUploaded.add(eventFNC["fileUploaded"] = () => {
            this._addFile(fileEditAttachment, eventFNC);
        });
        if (await fileEditAttachment.processFile(files[0], this._actualAttachment)) {
            this._attachmentsContainer.appendChild(commentaryFrameAttachmentElement);
            this.attachmentAdded.trigger(this);

            // odstraní přílohu ze seznamu příloh a odstraní ji z DOMu
            fileEditAttachment.fileRemoved.add((objToRemove, _data) => {
                const qm = new MessageWindow(new LocalizedStringBuilder("GUI-599180")); //Odstranit?
                qm.okButtonCaption = "GUI-146838"; // Ano
                qm.noButtonCaption = "GUI-146840"; // Ne
                qm.type = MessageType.message;
                qm.show().then((r) => {
                    if (r.result == MessageButtonType.ok) {
                        this._removeFile(objToRemove);
                    }
                    this.fileRemovedMessageShowed.trigger(this, null);
                });
            });

            fileEditAttachment.uploadError.add(() => {
                this._addFileErrorInfo();
            });
        } else {
            fileEditAttachment.dispose();
            this._actualAttachment.dispose();
        }
    }

    private _addFileErrorInfo(): void {
        if (!this._fileError) {
            this._setSendButtonEnabled(false);

            this._fileError = true;
            this._fileErrorContainer = createElement({ classes: [this._cssClassErrorContainer] }) as HTMLDivElement;
            const fileErrorMessage = createElement({ classes: [this._cssClassErrorMessage, "icon", "web-warning-filled"] }) as HTMLDivElement;
            Localization.addLocData(fileErrorMessage, "GUI-688789"); // Některé soubory se nepodařilo nahrát.

            const reloadFilesBtn = createElement({ type: "button", classes: [this._cssClassErrorButton, "flat", "btn", "btn-small"] }) as HTMLButtonElement;
            Localization.addLocData(reloadFilesBtn, "GUI-688796"); // Nahrát znovu
            reloadFilesBtn.addEventListener("click", async (e: MouseEvent) => {
                EventHelper.stopImmediatePropagation(e);
                this._removeFileErrorInfo();
                await this._reUpload();
            });

            this.appendChild(this._fileErrorContainer);
            this._fileErrorContainer.appendChild(fileErrorMessage);
            this._fileErrorContainer.appendChild(reloadFilesBtn);
            this.valueChanged.trigger(this, this._commentInputBox.innerText);
        }
    }

    private _removeFileErrorInfo(): void {
        if (this._fileError) {
            this._fileErrorContainer.remove();
            this._fileError = false;
            this.valueChanged.trigger(this, this._commentInputBox.innerText);
        }
    }

    public clearAttachmentsList(): void {
        while (this._fileEditAttachmentsList.length > 0) {
            this._removeFile(this._fileEditAttachmentsList[0]);
        }
    }

    /**
     * přidá soubor do seznamu příloh komentáře, zaktivní odeslat, vyvolá událost valuechanged
     * @param objToAdd
     */
    private _addFile(objToAdd: CommentaryFrameAttachment, eventFNC: any): void {
        this._fileEditAttachmentsList.push(objToAdd);
        // upraví velikost kontejneru
        this.valueChanged.trigger(this, this._commentInputBox.innerText);
        if (!UploadManager.instance.isUploadRunningOnClass(this.attachmentClass))
            this._setSendButtonEnabled(true);
        objToAdd.fileUploaded.remove(eventFNC["fileUploaded"]);
        objToAdd.fileUploaded.remove(eventFNC["fileStartsUploading"]);
    }

    /**
     * odstraní soubor ze seznamu příloh komentáře
     * @param objToRemove
     */
    private _removeFile(objToRemove: CommentaryFrameAttachment): void {
        const index = this._fileEditAttachmentsList.indexOf(objToRemove);
        this._fileEditAttachmentsList.splice(index, 1);
        // pokud je to posledni priloha s error stavem, uz nema smysl zobrazovat errorContainer pro reload
        const noError = this._fileEditAttachmentsList.some(obj => obj.isValid == false);
        if (!noError)
            this._removeFileErrorInfo();
        objToRemove.dispose();
        if (this._fileEditAttachmentsList.length == 0 && this.getCommentText() == "")
            this._setSendButtonEnabled(false);
        this.valueChanged.trigger(this, this._commentInputBox.innerText);
    }

    /**zkusí znovu načíst přílohy, které se nepovedly uploadnout */
    private async _reUpload(): Promise<void> {
        for (const remObj of this._fileEditAttachmentsList) {
            if (remObj.isValid == false) {
                await remObj.reloadFile();
            }
        }
    }

    private _setContentEditableBasedOnRights(): void {
        this._commentInputBox.contentEditable = (this._canInsertComments ? "true" : "false");
        this.setAttachmentButtonEnabled(this._canInsertComments);
    }

    /**
     * odstraní attachmentbutton a jeho eventListenery
     */
    public removeAttachmentButton(): void {
        this._inputFile?.removeEventListener("click", this._eventListFnc["inputFileOnClick"]);
        this._inputFile?.removeEventListener("change", this._eventListFnc["inputFileOnChange"]);
        this._inputFile?.remove();
        this._attachmentButton?.removeEventListener("click", this._eventListFnc["attachmentButtonOnClick"]);
        this._attachmentButton?.remove();
    }

    /**
     * nastaví kurzor na konec
     */
    public setCarretAtTheEnd(): void {
        if (this.commentInputBox.childNodes.length === 0)
            return;
        let offsetStart;
        const range = document.createRange();
        const selection = window.getSelection();
        if (this.commentInputBox.childNodes.length > 1)
            offsetStart = 1;
        else
            offsetStart = this.commentInputBox.textContent.length;
        range.setStart(this.commentInputBox.lastChild, offsetStart);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    public dispose(): void {
        this._deleteButton?.removeEventListener("click", this._eventListFnc["clickDeleteButton"]);
        this._sendCommentButton?.removeEventListener("click", this._eventListFnc["clickSendButton"]);
        this._commentInputBox?.removeEventListener("input", this._eventListFnc["inputInput"]);
        if (this._attachmentButton)
            this.removeAttachmentButton();
    }
    //#endregion Methods
}

registerCustomElement("tsc-commentaryframeinput", CommentaryFrameInput);
